import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

function resStart(r) {
  const p = r[r.type] || {};
  return {
    flight: p.departure_time,
    hotel: p.check_in,
    car: p.pickup_time,
    event: p.start_time,
  }[r.type] || "";
}

function resLabel(r) {
  const p = r[r.type] || {};
  const when = (resStart(r) || "").slice(0, 10);
  const what = {
    flight: [p.airline, p.flight_number, p.departure_airport && p.arrival_airport ? `${p.departure_airport}→${p.arrival_airport}` : null].filter(Boolean).join(" "),
    hotel: p.hotel_name,
    car: p.vendor,
    event: p.event_name,
  }[r.type] || r.type;
  return `${r.type.toUpperCase()} · ${what || "—"}${when ? ` · ${when}` : ""}`;
}

export default function SplitTripDialog({ trip, reservations, onClose, onSplit }) {
  const [mode, setMode] = useState("date");
  const [date, setDate] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const dateMoveCount = useMemo(
    () => reservations.filter((r) => (resStart(r) || "").slice(0, 10) >= (date || "").slice(0, 10)).length,
    [reservations, date]
  );

  const valid = mode === "date"
    ? !!date && dateMoveCount > 0 && dateMoveCount < reservations.length
    : selected.size > 0 && selected.size < reservations.length;

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const body = { new_trip_name: newName.trim() || null };
      if (mode === "date") body.date = date;
      else body.reservation_ids = [...selected];
      const { data } = await api.post(`/trips/${trip.id}/split`, body);
      toast.success(`Split off ${data.moved} reservation${data.moved !== 1 ? "s" : ""}`);
      onSplit(data.new_trip);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Split failed");
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-none border-sand-300" data-testid="split-trip-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-medium text-2xl tracking-tight">
            Split trip
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-2">
            <button
              onClick={() => setMode("date")}
              className={`btn-swiss text-xs flex-1 justify-center ${mode === "date" ? "btn-primary" : "btn-ghost"}`}
              data-testid="split-mode-date"
            >
              By date
            </button>
            <button
              onClick={() => setMode("pick")}
              className={`btn-swiss text-xs flex-1 justify-center ${mode === "pick" ? "btn-primary" : "btn-ghost"}`}
              data-testid="split-mode-pick"
            >
              Pick reservations
            </button>
          </div>

          {mode === "date" ? (
            <div>
              <label className="label-tag block mb-2">Everything starting on/after</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="swiss-input w-full text-sm py-2"
                data-testid="split-date-input"
              />
              {date && (
                <p className="font-mono text-[11px] text-sand-500 mt-2" data-testid="split-date-preview">
                  {dateMoveCount} of {reservations.length} reservations would move
                  {(dateMoveCount === 0 || dateMoveCount === reservations.length) && " — both sides must be non-empty"}
                </p>
              )}
            </div>
          ) : (
            <div className="max-h-56 overflow-y-auto border border-sand-200 divide-y divide-sand-100">
              {reservations.map((r) => (
                <label key={r.id} className="flex items-center gap-3 px-3 py-2.5 text-xs font-mono cursor-pointer hover:bg-sand-50">
                  <input
                    type="checkbox"
                    checked={selected.has(r.id)}
                    onChange={() => toggle(r.id)}
                    data-testid={`split-check-${r.id}`}
                  />
                  <span className="truncate">{resLabel(r)}</span>
                </label>
              ))}
            </div>
          )}

          <div>
            <label className="label-tag block mb-2">New trip name</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={`${trip.name} (split)`}
              className="swiss-input w-full text-sm py-2"
              data-testid="split-name-input"
            />
          </div>
        </div>

        <DialogFooter>
          <button onClick={onClose} className="btn-swiss btn-ghost text-sm" data-testid="split-cancel-btn">Cancel</button>
          <button
            onClick={submit}
            disabled={busy || !valid}
            className="btn-swiss btn-primary text-sm disabled:opacity-50"
            data-testid="split-confirm-btn"
          >
            {busy ? "Splitting…" : "Split"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
